import { useEffect, useRef, useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useOrganizationsService } from '@restworld/data-services';
import { getAttributionFromCookie, useUTMSource } from '@restworld/utils-common';
import {
  orgOnboardingProspectIdAtom,
  orgOnboardingRestaurantDataAtom,
  orgOnboardingJPDataAtom,
  orgOnboardingPartnerInfoAtom,
} from './use-organization-onboarding';
import { isDeferredBillingCampaign } from './deferred-billing';
import { tryForFreeAssignmentAtom } from './try-for-free-variant';
import {
  activeAddressIdAtom,
  activeRestaurantAtom,
} from '../restaurants/restaurant-onboarding-modal/google-address-input-result';
import { restaurantInformationDataAtom } from '../restaurants/restaurant-onboarding-modal/use-restaurant-onboarding-data-query';

type ProspectUpsertParams = {
  last_completed_step: string;
  gmaps_place_id?: string;
  restaurant_name?: string;
  address?: string;
  country?: string;
  contact_name?: string;
  contact_surname?: string;
  contact_email?: string;
  contact_phone?: string;
  job_position_data?: Record<string, unknown>;
  utm_source?: string;
  partner_code?: string;
  flow_variant?: string;
  // Meta match signals for the server-side `RestaurantSelected` CAPI conversion
  // (fired backend-side at the restaurant_selected step). The backend rebuilds
  // `fbc` from `fbclid`; `fbp` is forwarded raw. Read from the rw_attribution /
  // _fbp cookies, same source the org-create payload uses.
  fbclid?: string;
  fbp?: string;
};

/**
 * Lightweight hook for imperatively upserting prospect data.
 * Can be called from any component (step footers, etc.).
 *
 * Automatically collects all available data from onboarding atoms and merges
 * with the params passed by the caller. The caller only needs to specify
 * `last_completed_step` — the rest is filled from atom state.
 */
export const useProspectUpsert = () => {
  const [prospectId, setProspectId] = useAtom(orgOnboardingProspectIdAtom);
  const restaurantData = useAtomValue(orgOnboardingRestaurantDataAtom);
  const jpData = useAtomValue(orgOnboardingJPDataAtom);
  const activeAddressId = useAtomValue(activeAddressIdAtom);
  const activeRestaurantName = useAtomValue(activeRestaurantAtom);
  const restaurantInfo = useAtomValue(restaurantInformationDataAtom);
  const orgService = useOrganizationsService();
  const { utmSource: rawUtmSource, partnerCode } = useUTMSource();
  const utmSource = Array.isArray(rawUtmSource) ? rawUtmSource[0] : rawUtmSource;
  const partnerInfo = useAtomValue(orgOnboardingPartnerInfoAtom);
  const tryForFreeAssignment = useAtomValue(tryForFreeAssignmentAtom);
  // Only a Try for Free onboarding carries its variant; the backend keeps these prospects
  // after signup instead of deleting them (RW-411).
  const flowVariant = isDeferredBillingCampaign(partnerInfo) ? tryForFreeAssignment?.variant : undefined;

  const upsertProspect = useCallback(
    (overrides?: Partial<ProspectUpsertParams>) => {
      // Generate or reuse UUID
      let id = prospectId;
      if (!id) {
        id = crypto.randomUUID();
        setProspectId(id);
      }

      // First-touch attribution (rw_attribution + _fbp cookies) for the
      // server-side RestaurantSelected CAPI match.
      const attribution = getAttributionFromCookie();

      // Collect all available data from atoms
      const restaurants = restaurantData?.restaurants as Record<string, unknown> | undefined;
      const contacts = restaurantData?.employer_user_contacts;
      const info = restaurantInfo as Record<string, unknown> | undefined;

      // Determine step from available data (can be overridden by caller)
      let lastStep = 'restaurant_selected';
      if (jpData) lastStep = 'job_position_creation';
      else if (restaurantData) lastStep = 'restaurant_creation';

      // Build payload by merging atom state + enrichment data + caller overrides
      const params: ProspectUpsertParams & { id: string } = {
        id,
        last_completed_step: lastStep,
        // flow_variant: set only for the Try for Free A/B test ('default' / 'simplified');
        // null for everyone else, as since the May-June 'jp_first' experiment ended.
        ...(flowVariant ? { flow_variant: flowVariant } : {}),
        // From Google Maps selection
        gmaps_place_id: activeAddressId || undefined,
        restaurant_name: activeRestaurantName || undefined,
        // From enrichment data (getAdditionalRestaurantOnboardingData)
        ...(info && Object.keys(info).length > 0
          ? {
              restaurant_name: (info.restaurant_name as string) || activeRestaurantName || undefined,
              address: (info.address as string) || undefined,
              country: (info.country as string) || undefined,
            }
          : {}),
        // From Step 1 form (overrides enrichment data with final user-edited values)
        ...(restaurants
          ? {
              restaurant_name: restaurants.restaurant_name as string | undefined,
              address: restaurants.address as string | undefined,
              gmaps_place_id: restaurants.gmaps_place_id as string | undefined,
              country: restaurants.country as string | undefined,
            }
          : {}),
        ...(contacts
          ? {
              contact_name: contacts.name,
              contact_surname: contacts.surname,
              contact_email: contacts.email,
              contact_phone: contacts.phone_number,
            }
          : {}),
        // From Step 2 (JP data)
        ...(jpData ? { job_position_data: jpData as Record<string, unknown> } : {}),
        // From URL utm_source query param (captured by UTMSourceProvider)
        ...(utmSource ? { utm_source: utmSource } : {}),
        // From URL ?partner= query param (captured by UTMSourceProvider)
        ...(partnerCode ? { partner_code: partnerCode } : {}),
        // Meta match signals for the server-side RestaurantSelected CAPI event.
        // fbclid -> backend rebuilds `fbc`; fbp forwarded raw. (fbp is usually
        // absent on getting-started since the pixel does not load there.)
        ...(attribution.fbclid ? { fbclid: attribution.fbclid } : {}),
        ...(attribution.fbp ? { fbp: attribution.fbp } : {}),
        // Caller overrides (highest priority)
        ...overrides,
      };

      // Fire-and-forget
      orgService.upsertProspect(params).catch(() => {
        return null;
      });
    },
    [
      prospectId,
      setProspectId,
      restaurantData,
      jpData,
      activeAddressId,
      activeRestaurantName,
      restaurantInfo,
      orgService,
      utmSource,
      partnerCode,
      flowVariant,
    ]
  );

  return { upsertProspect, prospectId };
};

/**
 * Reactive hook that auto-syncs prospect data to the backend whenever
 * the onboarding atoms change. Should be called once in the top-level
 * OrganizationOnboarding component.
 *
 * Reactive triggers:
 * - When restaurantData atom changes (Step 1 form completed) → upsert
 * - When jpData atom changes (Step 2 completed) → upsert
 *
 * NOTE: The earliest funnel events (Google Maps selection, enrichment) are
 * tracked imperatively via useProspectUpsert, called from each "Continue"
 * button handler.
 */
export const useProspectTracking = () => {
  const { upsertProspect } = useProspectUpsert();
  const restaurantData = useAtomValue(orgOnboardingRestaurantDataAtom);
  const jpData = useAtomValue(orgOnboardingJPDataAtom);

  // Ref to skip initial mount (atoms restored from localStorage on refresh)
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Trigger on first meaningful data from either step (restaurantData
    // arrives first, then jpData).
    if (!restaurantData && !jpData) return;

    // Skip initial mount (atoms restored from localStorage on refresh)
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }

    upsertProspect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantData, jpData]);
};
